import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { explain } from '@/shared/api/problem'
import type { SurveyVocabularyDto } from '@/shared/api/types'
import { getPoliticsVocabulary, getVocabulary } from './api'

/**
 * The served code lists, fetched once per page load.
 *
 * ## Why this exists at all
 *
 * PollSphere's filter dropdowns were hand-written copies of its Prisma enums.
 * They drifted, so the UI offered `STATION`, `TRACKING`, `OPPOSITION_RESEARCH`,
 * `YOUTH`, `WOMEN` and `RURAL`; the server had never heard of any of them, and
 * selecting one returned an empty list with no error. That is the worst failure
 * mode available — it looks like an answer. `GET /surveys/vocabulary` exists
 * precisely so no screen has to keep a second copy, and this hook is how every
 * picker in the feature reaches it.
 *
 * ## Why the cache is at module scope and not in a context
 *
 * These lists are the server's enum constants. They cannot change between two
 * navigations inside one page load, so refetching on every mount is one request
 * per navigation for data that is effectively static — and every picker on a
 * screen would issue its own. The cache is a plain module store rather than a
 * provider because there is nothing to configure and nothing to scope: a second
 * instance of it would itself be the drift this file exists to prevent.
 *
 * ## Why it is a store and not a promise plus per-hook state
 *
 * Because a retry has to heal every picker, not the one that was clicked. A
 * screen shows two or three of these; if each kept its own copy of the result,
 * pressing "try again" under the category select would repopulate that select
 * and leave the method select next to it still saying the list could not be
 * loaded — from the same successful response. One snapshot, every subscriber.
 *
 * ## Why a failure is not cached
 *
 * A resolved list is kept forever; a rejection is not. After a failure the next
 * mount (or an explicit retry) starts a fresh request. A cached failure would
 * leave every picker in the session dead until a full page reload, which is a
 * worse answer than one extra request.
 *
 * ## Why the politics vocabulary is served from this file too
 *
 * `GET /politics/vocabulary` publishes the register's four code lists and needs
 * exactly the same store: one request per page load, one snapshot, a retry that
 * heals every picker. `getPoliticsVocabulary()` lives in `polling/api.ts`, and
 * politics imports both the call and {@link usePoliticsVocabulary} from here
 * rather than the call being moved — moving it would mean editing two API
 * modules to relocate a function that is already correct, and the store below is
 * the thing worth sharing. Two copies of THAT would be two caches, two retries
 * and two chances to get the loading-versus-empty distinction wrong.
 */

export type VocabularyKey = keyof SurveyVocabularyDto

/** The four code lists `GET /politics/vocabulary` serves. */
export type PoliticsVocabularyDto = Record<string, string[]>
export type PoliticsVocabularyKey =
  'partyStatus' | 'coalitionStatus' | 'candidateStatus' | 'partyCoalitionRole'

/**
 * The sentence a picker shows in place of its options.
 *
 * A select with no options and no explanation is indistinguishable from "no
 * categories exist" — and that reading is not just wrong, it is the same
 * looks-like-an-answer failure the hook exists to stop, arriving by a different
 * road. One sentence, defined once, so no caller has to phrase it.
 */
export const CODE_LIST_UNAVAILABLE =
  'The code list could not be loaded from the server. This is not the same as there being '
  + 'none — nothing can be chosen until the request succeeds.'

interface Snapshot<T> {
  vocab: T | null
  loading: boolean
  error: string | null
}

interface Store<T> {
  subscribe: (listener: () => void) => () => void
  read: () => Snapshot<T>
  start: () => void
}

/**
 * One module-scope cache per endpoint.
 *
 * A factory rather than two hand-written copies: the survey and politics
 * vocabularies differ only in which URL they read, and every property that
 * matters here — the one-request collapse, the uncached rejection, the
 * loading-starts-true rule — is a property of the store, not of the payload.
 */
function makeStore<T>(fetcher: () => Promise<T>): Store<T> {
  /**
   * `loading` starts TRUE, before any request exists.
   *
   * The first subscriber's effect starts the fetch a tick after its first render,
   * and a snapshot claiming "not loading, no options" in that gap would flash
   * {@link CODE_LIST_UNAVAILABLE} on every screen with a picker on it.
   */
  let snapshot: Snapshot<T> = { vocab: null, loading: true, error: null }
  const listeners = new Set<() => void>()
  let inflight = false

  /** A new object each time, because `useSyncExternalStore` compares by identity. */
  const publish = (next: Snapshot<T>) => {
    snapshot = next
    for (const listener of listeners) listener()
  }

  return {
    subscribe(listener) {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
    read: () => snapshot,
    start() {
      // Already answered, or a request is already in flight for everybody. Either
      // way there is nothing to start — this is what collapses the three pickers
      // on a screen into one request, and makes a retry idempotent.
      if (snapshot.vocab || inflight) return
      inflight = true
      publish({ vocab: null, loading: true, error: null })
      fetcher()
        .then((vocab) => publish({ vocab, loading: false, error: null }))
        .catch((e) => publish({ vocab: null, loading: false, error: explain(e) }))
        .finally(() => { inflight = false })
    },
  }
}

const surveyStore = makeStore<SurveyVocabularyDto>(getVocabulary)
const politicsStore = makeStore<PoliticsVocabularyDto>(getPoliticsVocabulary)

function useStore<T>(store: Store<T>): Snapshot<T> & { reload: () => void } {
  const state = useSyncExternalStore(store.subscribe, store.read)
  useEffect(() => { store.start() }, [store])
  const reload = useCallback(() => store.start(), [store])
  return { ...state, reload }
}

export interface VocabularyState extends Snapshot<SurveyVocabularyDto> {
  /** Retry after a failure. A no-op once the lists are loaded — nothing to retry. */
  reload: () => void
}

export function useVocabulary(): VocabularyState {
  return useStore(surveyStore)
}

export interface PoliticsVocabularyState extends Snapshot<PoliticsVocabularyDto> {
  reload: () => void
}

/**
 * `partyStatus`, `coalitionStatus`, `candidateStatus` and `partyCoalitionRole`.
 *
 * Every enum on the server ends in an `UNKNOWN` sentinel the controller strips
 * before serving, so this list never offers it. A picker that did would post a
 * status parsing back to "we could not read this", which is not a thing any
 * party, coalition or candidate can be.
 */
export function usePoliticsVocabulary(): PoliticsVocabularyState {
  return useStore(politicsStore)
}

export interface CodesState {
  codes: string[]
  loading: boolean
  error: string | null
  /**
   * True when there is nothing to offer and the load is over — a failed request,
   * or a key the server served empty. Both mean the same thing to a picker: show
   * {@link CODE_LIST_UNAVAILABLE} rather than an empty select.
   */
  unavailable: boolean
  reload: () => void
}

/**
 * One list, for one picker.
 *
 * `codes` is empty while loading and after a failure. That empty array is never
 * a claim that the list is empty — `loading` and `unavailable` are what a caller
 * reads to tell the three cases apart, and rendering the options without
 * checking them is the bug `unavailable` is returned to prevent.
 */
export function useCodes(key: VocabularyKey): CodesState {
  const { vocab, loading, error, reload } = useVocabulary()
  const codes = vocab?.[key] ?? []
  return { codes, loading, error, unavailable: !loading && codes.length === 0, reload }
}

/**
 * One register list, for one picker. Same three states as {@link useCodes}.
 *
 * The payload is a `Record<string, string[]>` rather than a named record type, so
 * a key the server has stopped serving reads as an absent list rather than
 * failing to compile — which is the right shape here, because `unavailable`
 * already renders that case as a sentence.
 */
export function usePoliticsCodes(key: PoliticsVocabularyKey): CodesState {
  const { vocab, loading, error, reload } = usePoliticsVocabulary()
  const codes = vocab?.[key] ?? []
  return { codes, loading, error, unavailable: !loading && codes.length === 0, reload }
}
